import { useEffect, useState } from "react";
import LiveDirectionsMap from "../../components/LiveDirectionsMap";
import { DriverPosition } from "../hooks/useDriverLocationSharing";
import { mapsDirectionsUrl, telHref } from "../utils";

export interface MapTarget {
  kind: "restaurant" | "customer";
  name: string;
  address: string;
  lat: number;
  lng: number;
  phone?: string | null;
}

interface Props {
  target: MapTarget;
  currentPosition: DriverPosition | null;
  onClose: () => void;
}

export default function DriverMapModal({ target, currentPosition, onClose }: Props) {
  // Rota açılışta bir kez hesaplanır; canlı konum LiveDirectionsMap'in kendi izleyicisiyle
  // güncellenir. Origin her GPS güncellemesinde değişseydi harita her seferinde baştan kurulurdu.
  const [origin, setOrigin] = useState<DriverPosition | null>(currentPosition);
  const [locating, setLocating] = useState(!currentPosition);

  useEffect(() => {
    if (origin || !navigator.geolocation) {
      setLocating(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const isPickup = target.kind === "restaurant";
  const destination = { lat: target.lat, lng: target.lng };

  return (
    <div className="dp-map-wrap" onClick={onClose}>
      <div className="dp-map" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="dp-map__head">
          <div
            className="dp-map__icon"
            style={{ background: isPickup ? "#eef2ff" : "#fff7ed", color: isPickup ? "#4f46e5" : "#ea580c" }}
          >
            <i className={`bi ${isPickup ? "bi-shop" : "bi-geo-alt-fill"}`}></i>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="dp-map__title">{isPickup ? "Restorana git" : "Müşteriye git"} · {target.name}</div>
            <div className="dp-map__sub">{target.address || "Adres bilgisi yok"}</div>
          </div>
          <button className="dp-icon-btn" onClick={onClose} aria-label="Kapat">
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="dp-map__body">
          {locating ? (
            <div className="dp-center" style={{ paddingTop: 80 }}>
              <div className="spinner-border" style={{ color: "#ea580c" }} role="status" />
              <div style={{ marginTop: 12, fontSize: ".85rem", color: "#6b7280" }}>Konumunuz alınıyor...</div>
            </div>
          ) : origin ? (
            <LiveDirectionsMap origin={origin} destination={destination} height="100%" />
          ) : (
            <iframe
              title="Hedef konum"
              width="100%" height="100%"
              style={{ border: 0, display: "block" }}
              loading="lazy" allowFullScreen
              src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${target.lat},${target.lng}&zoom=16`}
            />
          )}
        </div>

        <div className="dp-map__foot">
          <a className="dp-btn dp-btn--primary" href={mapsDirectionsUrl(target.lat, target.lng)} target="_blank" rel="noopener noreferrer">
            <i className="bi bi-cursor-fill"></i> Google Maps ile navigasyon
          </a>
          {target.phone ? (
            <a className="dp-btn dp-btn--ghost" href={telHref(target.phone)}>
              <i className="bi bi-telephone-fill"></i> Ara
            </a>
          ) : (
            <button className="dp-btn dp-btn--ghost" onClick={onClose}>Kapat</button>
          )}
        </div>
      </div>
    </div>
  );
}
